import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box, Card, PasswordInput, Button, Text, Alert, Stack, List, Loader,
} from '@mantine/core'
import {
  IconAlertCircle, IconCheck, IconLock, IconShieldLock, IconWand, IconAlertTriangle,
} from '@tabler/icons-react'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { authApi } from '@/api/auth'
import { APP_VERSION } from '@/version'
import { MENSAGEM_SENHA_RECUSADA } from '@/pages/auth/changePasswordOutcome'

function validatePassword(pw: string): string[] {
  const errors: string[] = []
  if (pw.length < 10) errors.push('Mínimo 10 caracteres')
  if (!/\d/.test(pw)) errors.push('Pelo menos 1 número')
  if (!/[a-zA-Z]/.test(pw)) errors.push('Pelo menos 1 letra')
  return errors
}

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '0123456789'
const SPECIAL = '!@#$%&*-_=+'

function generatePassword(): string {
  const all = UPPER + LOWER + DIGITS + SPECIAL
  const arr = new Uint8Array(14)
  crypto.getRandomValues(arr)
  const chars = Array.from(arr).map((b) => all[b % all.length])
  const mandatory = [
    UPPER[Math.floor(Math.random() * UPPER.length)],
    LOWER[Math.floor(Math.random() * LOWER.length)],
    DIGITS[Math.floor(Math.random() * DIGITS.length)],
    SPECIAL[Math.floor(Math.random() * SPECIAL.length)],
  ]
  mandatory.forEach((c, i) => { chars[i] = c })
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

type PageState = 'validating' | 'invalid' | 'form' | 'success'

export function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [state, setState] = useState<PageState>('validating')
  const [invalidReason, setInvalidReason] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [platformErrorCode, setPlatformErrorCode] = useState<string | null>(null)
  const [reportState, setReportState] =
    useState<'idle' | 'sending' | 'created' | 'exists' | 'refused' | 'error'>('idle')
  const [reportProtocol, setReportProtocol] = useState('')

  const handleReportPlatformError = async () => {
    setReportState('sending')
    try {
      const res = await authApi.reportPlatformError({
        token,
        errorCode: platformErrorCode ?? 'server_error',
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      })
      if (res.status === 'refused') {
        setReportState('refused')
      } else {
        setReportProtocol(res.protocol_number ?? '')
        setReportState(res.status === 'exists' ? 'exists' : 'created')
      }
    } catch {
      setReportState('error')
    }
  }

  const pwErrors = newPw ? validatePassword(newPw) : []
  const mismatch = confirmPw && newPw !== confirmPw
  const canSubmit = newPw && confirmPw && !mismatch && pwErrors.length === 0

  useEffect(() => {
    if (!token) {
      setInvalidReason('Nenhum token fornecido.')
      setState('invalid')
      return
    }
    authApi.validateResetToken(token)
      .then(() => setState('form'))
      .catch((err) => {
        const rawDetail = err?.response?.data?.detail
        const detail = typeof rawDetail === 'string' ? rawDetail : ''
        if (detail === 'token_expired') {
          setInvalidReason('Este link expirou. Solicite um novo reset de senha.')
        } else if (detail === 'token_used') {
          setInvalidReason('Este link já foi utilizado. Solicite um novo reset se necessário.')
        } else {
          setInvalidReason('Link inválido ou expirado.')
        }
        setState('invalid')
      })
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError('')
    setPlatformErrorCode(null)
    setReportState('idle')
    setLoading(true)
    try {
      await authApi.resetPassword(token, newPw)
      setState('success')
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      const detail = typeof rawDetail === 'string' ? rawDetail : ''
      if (detail === 'token_expired') {
        setError('O link expirou. Solicite um novo reset de senha.')
      } else if (detail === 'token_used') {
        setError('Este link já foi utilizado.')
      } else if (detail === 'password_too_short') {
        setError('A nova senha deve ter no mínimo 10 caracteres.')
      } else if (detail === 'password_rejected_by_directory') {
        // Recusa da senha, nao falha da plataforma: nao marcar platformErrorCode
        // (isso abre o fluxo de "reportar erro" para algo que o usuario resolve
        // sozinho escolhendo outra senha).
        setError(MENSAGEM_SENHA_RECUSADA)
      } else if (detail.startsWith('kanidm_provisioning_failed')) {
        setError('Erro ao configurar credenciais. Tente novamente em alguns minutos.')
        setPlatformErrorCode('kanidm_provisioning_failed')
      } else {
        setError('Erro ao redefinir a senha. Tente novamente.')
        if ((err?.response?.status ?? 0) >= 500) setPlatformErrorCode(detail || 'server_error')
      }
    } finally {
      setLoading(false)
    }
  }

  const renderContent = () => {
    if (state === 'validating') {
      return (
        <Stack align="center" gap="md" py="xl">
          <Loader color="#098d93" size="md" />
          <Text size="sm" c="#b6c2c9">Validando link de reset...</Text>
        </Stack>
      )
    }

    if (state === 'invalid') {
      return (
        <>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <IconAlertTriangle size={22} color="#ff6b6b" />
            <Text fw={700} size="lg" c="#e9ecef">Link inválido</Text>
          </Box>
          <Text size="sm" c="#b6c2c9" mt="sm" mb="lg" lh={1.5}>
            {invalidReason}
          </Text>
          <Button
            fullWidth variant="subtle" color="teal" size="md" radius="sm"
            onClick={() => navigate('/auth/forgot-password')}
            style={{ fontWeight: 700, fontSize: 13 }}
          >
            Solicitar novo reset
          </Button>
          <Text
            size="xs" c="#098d93" ta="center" mt="md" fw={600}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/login')}
          >
            Voltar ao login
          </Text>
        </>
      )
    }

    if (state === 'success') {
      return (
        <>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <IconCheck size={22} color="#098d93" />
            <Text fw={700} size="lg" c="#e9ecef">Senha redefinida!</Text>
          </Box>
          <Text size="sm" c="#b6c2c9" mt="sm" mb="lg" lh={1.5}>
            Sua senha foi redefinida com sucesso. Você já pode acessar o sistema com a nova senha.
          </Text>
          <Button
            fullWidth size="md" radius="sm"
            onClick={() => navigate('/login')}
            style={{
              backgroundColor: 'var(--ai-brand-verde)',
              border: 'none',
              fontWeight: 700,
              fontSize: 13,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#077a80' }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)' }}
          >
            Ir para o login
          </Button>
        </>
      )
    }

    // state === 'form'
    return (
      <>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <IconShieldLock size={22} color="#098d93" />
          <Text fw={700} size="lg" c="#e9ecef">Redefinir senha</Text>
        </Box>
        <Text size="xs" c="#6c757d" mb="lg">
          Escolha uma nova senha para sua conta
        </Text>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" radius="sm"
            styles={{ root: { backgroundColor: 'rgba(255, 91, 87, 0.1)', border: '1px solid rgba(255, 91, 87, 0.2)' } }}
          >
            {error}
          </Alert>
        )}

        {platformErrorCode && (
          <Box mb="md">
            {(reportState === 'idle' || reportState === 'sending' || reportState === 'error') && (
              <>
                <Button
                  variant="light" color="gray" fullWidth
                  leftSection={<IconAlertTriangle size={16} />}
                  loading={reportState === 'sending'}
                  onClick={handleReportPlatformError}
                >
                  Reportar erro para a Infraestrutura
                </Button>
                {reportState === 'error' && (
                  <Text size="xs" c="red" mt={6}>Não foi possível reportar agora. Tente novamente.</Text>
                )}
              </>
            )}
            {(reportState === 'created' || reportState === 'exists') && (
              <Alert icon={<IconCheck size={16} />} color="teal" radius="sm">
                Chamado {reportProtocol} aberto. O time DevOps foi notificado.
              </Alert>
            )}
            {reportState === 'refused' && (
              <Alert icon={<IconAlertTriangle size={16} />} color="yellow" radius="sm">
                Não foi possível abrir o chamado para este acesso. Procure o seu supervisor.
              </Alert>
            )}
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <PasswordInput
              label="Nova senha"
              placeholder="Escolha sua nova senha"
              value={newPw}
              onChange={e => { setNewPw(e.currentTarget.value); setConfirmPw('') }}
              leftSection={<IconLock size={16} />}
              required
              size="md"
              styles={inputStyles}
              error={newPw && pwErrors.length > 0 ? ' ' : undefined}
              data-sensitive="true"
              wrapperProps={{ 'data-sensitive': 'true' }}
            />
            <Button
              variant="subtle"
              size="compact-xs"
              color="teal"
              leftSection={<IconWand size={13} />}
              onClick={() => {
                const pw = generatePassword()
                setNewPw(pw)
                setConfirmPw(pw)
              }}
              style={{ alignSelf: 'flex-start', marginTop: -4, fontSize: 11, fontWeight: 600 }}
            >
              Sugerir senha forte
            </Button>
            {newPw && pwErrors.length > 0 && (
              <List size="xs" spacing={2}
                styles={{ item: { color: '#ff6b6b', fontSize: 11 } }}
              >
                {pwErrors.map(err => <List.Item key={err}>{err}</List.Item>)}
              </List>
            )}
            {newPw && pwErrors.length === 0 && (
              <Text size="xs" c="#098d93" fw={600}>
                <IconCheck size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Senha valida
              </Text>
            )}
            <PasswordInput
              label="Confirmar nova senha"
              placeholder="Repita a nova senha"
              value={confirmPw}
              onChange={e => setConfirmPw(e.currentTarget.value)}
              leftSection={<IconLock size={16} />}
              required
              size="md"
              styles={inputStyles}
              error={mismatch ? 'As senhas não coincidem' : undefined}
              data-sensitive="true"
              wrapperProps={{ 'data-sensitive': 'true' }}
            />
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={!canSubmit}
              mt={8}
              size="md"
              radius="sm"
              style={{
                backgroundColor: canSubmit ? 'var(--ai-brand-verde)' : undefined,
                border: 'none',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: '0.02em',
                transition: 'all var(--ai-transition-base)',
              }}
              onMouseEnter={(e) => {
                if (canSubmit) e.currentTarget.style.backgroundColor = '#077a80'
              }}
              onMouseLeave={(e) => {
                if (canSubmit) e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)'
              }}
            >
              Redefinir Senha
            </Button>
          </Stack>
        </form>

        <Text
          size="xs" c="#098d93" ta="center" mt="lg" fw={600}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/login')}
        >
          Voltar ao login
        </Text>
      </>
    )
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle ambient glow */}
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Brand */}
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text size="xs" c="rgba(255,255,255,0.35)" mt={8} fw={600} tt="uppercase" style={{ letterSpacing: '0.08em' }}>
            Redefinir Senha
          </Text>
        </Stack>

        {/* Reset password card */}
        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          {renderContent()}

          <Text size="xs" c="#51585e" ta="center" mt="xl" fw={600}>
            ConsoleAI v{APP_VERSION}
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
